import React, {useEffect, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary} from '@mui/material';
import Checkbox from '../../Checkbox/Checkbox'
import {ReactComponent as ExpandMore} from '../../../icons/svg/expand-more.svg'
import {AppState} from '../../../store'
import {IAccordionExpanIconProps, IAppConsentsProps} from './AppConsentProps'
import {IAppConsentsModel} from '../../../models/settings/consent.fetch.model'
import {useDispatch, useSelector} from 'react-redux';
import {APP_CONSENTS_CHECK_ONE, APP_CONSENTS_TOGGLE_ALL} from '../../../store/actions/consent-action'
import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import {Environment} from '../../../environment/app-settings'
import { APP_COLOR, APP_FONT_SIZE } from '../../atoms/AtomsGlobalProps'

import './AppConsent.scss'
import Paragraph from "../../atoms/Paragraph/Paragraph";


const AccordeonExpandIcon = (props: IAccordionExpanIconProps) => {

    if (isEmpty(props.details)) {
        return null
    }

    return (
        <ExpandMore
            className={clsx(props.hasError ? 'app-consent-expand-icon expand-icon-has-error' : 'app-consent-expand-icon')}
        />
    )
}

const ConsentsList = ({appConsents}: { appConsents: IAppConsentsModel[] }) => {

    const dispatch = useDispatch()

    if (!appConsents.length) {
        return null
    }

    return (
        <div className='app-consent-list-outer-wrapper'>
            {appConsents.map((consent: IAppConsentsModel) => {

                const isChecked = consent.isChecked
                const heading = consent.isRequired ? `<span>*</span>${consent.title}` : consent.title

                const handleOnClick = (e: Event) => {
                    e.stopPropagation()
                    dispatch({
                        type: APP_CONSENTS_CHECK_ONE,
                        payload: consent.consentId,
                    })
                }

                return (
                    <AppSingleConsent
                        dataTestId={consent.isRequired ? 'app-consent-required-checkbox' : 'app-consent-optional-checkbox'}
                        key={consent.consentId}
                        heading={heading}
                        details={consent.text.length ?
                        <Paragraph className='app-consent-list-consent-detail'
                                   html={consent.text}></Paragraph> : undefined}
                        isChecked={isChecked}
                        handleCheckboxClick={(e: Event) => handleOnClick(e)}
                        hasError={consent.hasError}
                    />
                )
            })}
            <p className='app-consent-list-mandatory consent-text'><span>*</span>Zgody obligatoryjne</p>
            <p className='app-consent-list-consent-note'>

                Zaznaczając i przechodząc dalej potwierdzasz zapoznanie się z pełną treścią danego oświadczenia (zgody)
                dostępną po rozwinięciu. Ich złożenie (udzielenie) jest dobrowolne, jednak bez złożenia wskazanych jako
                „obligatoryjne” nie będziemy mogli rozpatrzyć Twojego wniosku o pożyczkę.
                <br/><br/>
                Masz prawo do ich odwołania w
                dowolnym momencie np. poprzez
                <a style={{fontWeight: "bold"}} href={"https://www.paytree.pl"}> paytree.pl/kontakt</a> (przy czym
                odwołanie nie działa wstecz).
            </p>
        </div>
    )
}

const AppSingleConsent = (props: IAppConsentsProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(Boolean(props.isOpen))

    return (
        <Accordion
            disableGutters
            className={clsx('app-consent-outer-wrapper', props.hasError && 'app-consent-has-error')}
            onChange={(e) => {
                e.stopPropagation()
                setIsOpen(!isOpen)
            }}
            expanded={isOpen}
        >
            <AccordionSummary
                expandIcon={<AccordeonExpandIcon hasError={props.hasError} details={props.details}
                                                 isOnTop={props.isOnTop}/>}
                classes={{root: 'app-consent-summary-wrapper', content: 'app-consent-summary-inner-wrapper'}}
            >
                <div className='app-consent-heading-wrapper' id={props.id ?? ""}>
                    <Checkbox
                        dataTestId={props.dataTestId}
                        isChecked={props.isChecked}
                        className={clsx('app-consent-checkbox', props.isOnTop && props.hasError && 'app-consent-checkbox-main-error')}
                        handleOnClick={props.handleCheckboxClick}
                        hasError={props.hasError}
                    />
                    {/*todo do zmiany na span - to nie poiwnien byc header - zostawiam h4 bo zle wspoldzialaja klasy */}
                    <h4
                        data-test-id={"app-consent-accordion-wrapper"}
                        dangerouslySetInnerHTML={{__html: props.heading}}
                        className={
                            clsx(
                                'app-consent-heading',
                                props.hasError && Environment.appConsentsErrorClassName,
                                props.isOnTop && 'app-consent-heading-primary',
                            )}
                    />
                    {!props.isOnTop && props.hasError && (
                        <b className='app-consent-error-message'>Zgoda obowiązkowa</b>
                    )}
                    <div className='app-consent-flex-breakline'/>
                    <Paragraph size={APP_FONT_SIZE.EXTRA_SMALL}
                               color={APP_COLOR.GRAY}
                               className='app-consent-aside-text'
                               html={props.aside || ''}
                               style={{gridArea: props.isOnTop ? 'app-consent-error-message' : 'app-consent-aside-text'}}
                    />
                </div>
            </AccordionSummary>
            <AccordionDetails
                classes={{root: 'app-consent-details-root'}}
            >
                {props.details}
            </AccordionDetails>
        </Accordion>
    )
}

const AppConsent = () => {
    const dispatch = useDispatch()
    const appConsents = useSelector((state: AppState) => state.consentState.appConsents)
    const [hasError, setHasError] = useState(false)
    const [isAllConsentsMarked, setIsAllConsentsMarked] = useState<boolean>(appConsents?.every((appConsent: IAppConsentsModel) => appConsent.isChecked))
    const [isChecked, setIsChecked] = useState(false)

    const handleOnClick = (e: Event) => {
        e.preventDefault()
        //todo
        const scrollDownElement = document.getElementsByName('form-submit-button')[0] as HTMLButtonElement

        setTimeout(() => {
            if (!isChecked === true) {
                scrollDownElement.scrollIntoView({behavior: "smooth", block: "center"}) // Jeśli checkbox "zaznacz wszystkie zgody" został zaznaczony, scrollujemy do przycisku "Dalej"
            }
        }, 300) //Opóźnienie po to aby zaczekać aż animacja otwierania / zamykania dodatkowych zgód się wykona.

        setIsChecked(!isChecked)

        dispatch({
            type: APP_CONSENTS_TOGGLE_ALL,
            payload: !isChecked
        })
    }


    useEffect(() => {
        setIsAllConsentsMarked(appConsents?.every((appConsent: IAppConsentsModel) => appConsent.isChecked))
        setHasError(appConsents?.some((appConsent: IAppConsentsModel) => appConsent.hasError))
    }, [appConsents])

    if (!appConsents.length) {
        return null
    }

    return (
        <div style={{height: 'fit-content'}} id='app-consents-outer-wrapper'>
            <AppSingleConsent
                id="app-consent-heading-wrapper"
                isChecked={isAllConsentsMarked}
                isOpen={isChecked}
                isOnTop={true}
                hasError={hasError}
                heading='Zaznaczam wszystkie oświadczenia'
                aside='Jeśli chcesz automatycznie zaznaczyć wszystkie poniższe.'
                details={<ConsentsList appConsents={appConsents}/>}
                handleCheckboxClick={(e: Event) => handleOnClick(e)}
            />
        </div>
    )
}

export default AppConsent
